import { api } from "@/api/client";
import type { FunnelStage, KpiMetric } from "@/api/types";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t, tOr } from "@/i18n";
import { formatInt, formatPercent } from "@/lib/format";
import { groupMetrics } from "@/lib/kpi";
import { includeTestSessions, kpiWindow, selectedTenant } from "@/state/filters";

// Success scorecard: the conversion funnel + the six KPIs (Nachfrage /
// Usability / Wirkung) as ACTUAL values — no target thresholds, no pass/fail.
// Percentages are relative to sessions (the server-side top of funnel: a
// session starts with the conversation, after the consent); loads and opens
// that start no conversation are GA4-only and called out as such. Subpage
// navigation doesn't double-count — the same-day localStorage session is reused.
//
// The backend ships German labels / notes keyed by a stable id. The view
// translates by that id and falls back to the backend text for an id it does
// not know, so a new metric never renders as a raw key.
const funnelLabel = (f: FunnelStage): string => tOr(`kpi.funnel.${f.key}`, f.label);
const metricLabel = (m: KpiMetric): string => tOr(`kpi.metric.${m.key}.label`, m.label);
const metricNote = (m: KpiMetric): string => tOr(`kpi.metric.${m.key}.note`, m.note);
const metricDenominator = (m: KpiMetric): string =>
  tOr(`kpi.metric.${m.key}.den`, m.denominator_label);

export function KpiScorecardView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.kpiScorecard(
        {
          window: kpiWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [kpiWindow.value, includeTestSessions.value, selectedTenant.value],
  );

  const wall = data?.privacy_wall;

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={lastFetched}
          loading={loading}
          error={error}
          onRefresh={refetch}
        />
      </div>

      <section class="card">
        <h2>{t("kpi.howToRead")}</h2>
        <p class="panel-help">{t("kpi.howToReadText")}</p>
      </section>

      <section class="card">
        <h2>{t("kpi.funnel")}</h2>
        <p class="panel-help">
          {t("kpi.funnelText", { total: data ? ` (${formatInt(data.sessions_total)})` : "" })}
        </p>
        <div class="funnel">
          {data?.funnel.map((f) => (
            <div class="funnel-row" key={f.key}>
              <div class="funnel-label">{funnelLabel(f)}</div>
              <div class="funnel-track">
                <div
                  class="funnel-fill"
                  style={`width: ${Math.round((f.pct_of_opened ?? 0) * 100)}%`}
                />
              </div>
              <div class="funnel-meta">
                <b class="hl-blue">{formatInt(f.count)}</b>
                {f.pct_of_opened == null ? "" : ` · ${formatPercent(f.pct_of_opened)}`}
              </div>
            </div>
          ))}
          {!data && <div class="muted">{t("common.loading")}</div>}
        </div>
      </section>

      <section class="card">
        <h2>{t("kpi.privacyWall")}</h2>
        <p class="panel-help">{t("kpi.privacyWallText")}</p>
        <div class="grid grid-3">
          <div class="card">
            <h3>{t("kpi.shown")}</h3>
            <div class="kpi">{wall ? formatInt(wall.shown) : "—"}</div>
            <div class="kpi-sub">{t("kpi.shownSub")}</div>
          </div>
          <div class="card">
            <h3>{t("kpi.accepted")}</h3>
            <div class="kpi">{wall ? formatInt(wall.accepted) : "—"}</div>
            <div class="kpi-sub">
              {t("kpi.acceptRate", {
                rate: wall?.accept_rate == null ? t("kpi.na") : formatPercent(wall.accept_rate),
              })}
            </div>
          </div>
          <div class="card" style="border-left: 4px solid var(--brand-orange);">
            <h3>{t("kpi.stopped")}</h3>
            <div class="kpi hl-orange">{wall ? formatInt(wall.stopped) : "—"}</div>
            <div class="kpi-sub">{t("kpi.stoppedSub")}</div>
          </div>
        </div>
        {wall && wall.pre_accepted > 0 && (
          <p class="muted" style="margin-top: 12px; font-size: 13px;">
            {t("kpi.preAccepted", { n: formatInt(wall.pre_accepted) })}
          </p>
        )}
      </section>

      {data &&
        groupMetrics(data.metrics).map((grp) => (
          <section class="card" key={grp.group}>
            <h2>{tOr(`kpi.group.${grp.group}`, grp.group)}</h2>
            <div class="grid grid-3">
              {grp.metrics.map((m) => (
                <MetricCard key={m.key} metric={m} />
              ))}
            </div>
          </section>
        ))}
    </div>
  );
}

function MetricCard({ metric }: { metric: KpiMetric }) {
  const hasValue = metric.measurable && metric.value != null;
  return (
    <div
      class="card"
      style={`border-left: 4px solid ${
        metric.measurable ? "var(--brand-blue)" : "var(--brand-beige)"
      };`}
    >
      <h3>{metricLabel(metric)}</h3>
      <div class={`kpi ${metric.measurable ? "" : "muted"}`}>
        {hasValue ? formatPercent(metric.value as number) : t("kpi.na")}
      </div>
      <div class="kpi-sub">
        {metric.measurable
          ? t("kpi.ofDenominator", {
              num: formatInt(metric.numerator),
              den: formatInt(metric.denominator),
              label: metricDenominator(metric),
            })
          : t("kpi.notMeasurable")}
      </div>
      <p class="muted" style="font-size: 12px; margin-top: 8px; line-height: 1.4;">
        {metricNote(metric)}
      </p>
    </div>
  );
}
